import { iconSizes, ToolTip } from "@hubrise/react-components"
import * as React from "react"
import { useTranslation } from "react-i18next"

import CatalogActions from "shared/components/CatalogActions"
import { useNavigationContext } from "shared/components/NavigationContext"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"
import { Editor } from "shared/hubrise/editor"
import { ICatalogSummary } from "shared/hubrise/models"
import { editPath } from "shared/utils/paths"

import {
  StyledCatalog,
  Header,
  Title,
  Name,
  Id,
  Controls,
  ButtonAppLink,
  DuplicationInProgress,
  StyledSpinner,
  Unsaved,
  UnsavedSymbol,
  Link,
  Label,
  LinkIcon,
} from "./Styles"

interface CatalogProps {
  catalogSummary: ICatalogSummary
  catalogSummaries: Array<ICatalogSummary>
  editor: Editor | undefined
  unsaved: boolean
}

const Catalog = ({ catalogSummary, catalogSummaries, editor, unsaved }: CatalogProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigateNoUpdate()
  const { getCatalogNavigation } = useNavigationContext()

  const [duplicationInProgress, setDuplicationInProgress] = React.useState(false)

  const catalogPath = (catalogId: string) => {
    const catalogNavigation = getCatalogNavigation(catalogId)
    return catalogNavigation?.editLastVisitedPath() ?? editPath(catalogId, "products")
  }

  return (
    <StyledCatalog data-testid="catalog-card">
      <Header>
        <Title onClick={() => navigate(catalogPath(catalogSummary.id))}>
          {unsaved && (
            <Unsaved>
              <ToolTip label={t("catalogs.unsaved_tooltip")}>
                <UnsavedSymbol>{t("general.unsaved_symbol")}</UnsavedSymbol>
              </ToolTip>
            </Unsaved>
          )}
          <Name>{catalogSummary.name}</Name>
          <Id>{catalogSummary.id}</Id>
        </Title>

        {duplicationInProgress && (
          <DuplicationInProgress>
            <StyledSpinner size={iconSizes._20} />
            {t("catalogs.actions.duplicate.in_progress")}
          </DuplicationInProgress>
        )}

        <Controls>
          <CatalogActions
            menuLink={({ onClick }) => (
              <Link onClick={onClick}>
                <Label>{t("catalogs.actions.title")}</Label>
                <LinkIcon />
              </Link>
            )}
            catalogSummary={catalogSummary}
            catalogSummaries={catalogSummaries}
            editor={editor}
            setDuplicationInProgress={setDuplicationInProgress}
          />
          <ButtonAppLink to={catalogPath(catalogSummary.id)}>{t("catalogs.open")}</ButtonAppLink>
        </Controls>
      </Header>
    </StyledCatalog>
  )
}

export default Catalog
